import { useSearch } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import type { ReactNode } from "react";

import { Alert } from "@/components/ui/alert";

import type { Page } from "../../api";
import { Chip } from "../../chips";
import { ItemLink, OptionLink } from "../../links";
import { TabPage } from "../../PageFrame";
import type { PageProps } from "../index";
import { dateGB, Footnote, money, Note, PAGE_SIZE, PagedTable, PageHead, usePage } from "../kit";
import { pageExtra, pageFilter } from "../params";

export interface StalledRow {
  funnel: string;
  item_id: string;
  item_name: string;
  option_id: string;
  option_name: string;
  phase: string | null;
  why: "no_movement" | "quote_expired";
  last_moved_at: string;
  days: number;
  quote_age_days: number | null;
  quote_expired: boolean;
  next_step: string;
  at_stake_gbp: number | null;
  no_current_cost: boolean;
}

export interface StalledData {
  stall_days: number;
  since: string;
  scope: { item_id: string | null; item_name: string | null; is_margin: boolean | null };
  stalled_n: number;
  rows: StalledRow[];
  items_not_moved: Array<{ item_id: string; item_name: string; days: number }>;
}

export const stalledSub = (days: number): string =>
  `Margin items and options with no movement for ${days}+ days, or an ingredient quote older than 90 days · biggest £ at stake first`;

export function pageSlice<Row>(rows: Row[], page: number): Page<Row> {
  const offset = (page - 1) * PAGE_SIZE;
  return {
    items: rows.slice(offset, page * PAGE_SIZE),
    total: rows.length,
    limit: PAGE_SIZE,
    offset,
  };
}

export function whyCell(row: StalledRow): ReactNode {
  return row.why === "quote_expired"
    ? <Chip tone="warn">Quote expired ({row.quote_age_days} days)</Chip>
    : "No movement";
}

export function stakeCell(row: StalledRow): ReactNode {
  return row.no_current_cost
    ? <Chip tone="muted" title="No current cost or annual quantity for this item yet">No current cost</Chip>
    : money(row.at_stake_gbp ?? 0, 0);
}

export const STALLED_COLUMNS: ColumnDef<StalledRow, unknown>[] = [
  {
    id: "item",
    header: "Item",
    cell: ({ row }) => (
      <ItemLink itemId={row.original.item_id} from="stalled">
        {row.original.item_name}
      </ItemLink>
    ),
  },
  {
    id: "option",
    header: "Option",
    cell: ({ row }) => (
      <span onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
        <OptionLink optionId={row.original.option_id} itemId={row.original.item_id} from="stalled">
          {row.original.option_name}
        </OptionLink>
      </span>
    ),
  },
  { id: "why", header: "Why", cell: ({ row }) => whyCell(row.original) },
  { accessorKey: "phase", header: "Phase", cell: ({ row }) => row.original.phase ?? "—" },
  { id: "last_movement", header: "Last movement", cell: ({ row }) => dateGB(row.original.last_moved_at) },
  { accessorKey: "days", header: "Days", meta: { align: "right" }, cell: ({ row }) => <b>{row.original.days}</b> },
  { accessorKey: "next_step", header: "Next step" },
  { id: "at_stake", header: "£ at stake (item)", meta: { align: "right" }, cell: ({ row }) => stakeCell(row.original) },
];

function ScopeNote({ data }: { data: StalledData }) {
  if (!data.scope.item_id || data.scope.is_margin !== false) return null;
  return <Footnote>{data.scope.item_name ?? "This item"} is not a margin item, so this page shows every margin item.</Footnote>;
}

function NotMovedAlert({ data }: { data: StalledData }) {
  const items = data.items_not_moved;
  if (items.length === 0) return null;
  return (
    <Alert>
      <span>
        <b>{items.length} item{items.length === 1 ? " has" : "s have"} not moved at all in {data.stall_days}+ days:</b>{" "}
        {items.map((item, index) => (
          <span key={item.item_id}>
            <ItemLink itemId={item.item_id} from="stalled">{item.item_name}</ItemLink> ({item.days} days)
            {index < items.length - 1 ? ", " : null}
          </span>
        ))}
      </span>
    </Alert>
  );
}

export function StalledBody({ data, page, onPage }: { data: StalledData; page: number; onPage: (next: number) => void }) {
  return (
    <>
      <ScopeNote data={data} />
      <NotMovedAlert data={data} />
      {data.rows.length === 0 ? (
        <Note>Nothing on the margin items has gone {data.stall_days}+ days without moving.</Note>
      ) : (
        <PagedTable<StalledRow>
          columns={STALLED_COLUMNS}
          data={pageSlice(data.rows, page)}
          page={page}
          onPage={onPage}
          caption="Stalled options"
          wrap
        />
      )}
      <Footnote>Days count from the last phase change, recorded since {dateGB(data.since)}.</Footnote>
    </>
  );
}

function stallDays(search: Record<string, unknown>): number {
  const value = pageFilter(search, "stall");
  return value === "30" || value === "60" ? Number(value) : 14;
}

export function StalledPage({ tab, areaName }: PageProps) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const [page, setPage] = usePage();
  return (
    <TabPage<StalledData>
      tab={tab}
      areaName={areaName}
      extra={pageExtra(tab.tabKey, search)}
      head={(data) => (
        <PageHead
          title={tab.label}
          sub={stalledSub(data?.stall_days ?? stallDays(search))}
          kpi={data ? { label: "Stalled options", value: data.stalled_n } : null}
        />
      )}
    >
      {(data) => <StalledBody data={data} page={page} onPage={setPage} />}
    </TabPage>
  );
}
